/**
 * staffDepartmentStore — Staff departments slice (Zustand).
 *
 *   GET    /api/staff/departments
 *   POST   /api/staff/departments
 *   PATCH  /api/staff/departments/:id
 *   DELETE /api/staff/departments/:id
 */
import { apiRequest } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const staffDepartmentInitialState = {
  staffDepartments: [],
  staffDepartmentsLoading: false,
  staffDepartmentsError: null,
  staffDepartmentMutationLoading: false,
  staffDepartmentMutationError: null,
};

/** The list endpoint returns a bare array; accept the wrapped shapes too. */
function normalizeDepartments(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return data?.items || data?.departments || [];
}

export function createStaffDepartmentActions(set, get) {
  async function mutate(path, method, body, successMessage) {
    set({
      staffDepartmentMutationLoading: true,
      staffDepartmentMutationError: null,
    });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({
        staffDepartmentMutationLoading: false,
        staffDepartmentMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const message = toDisplayString(err.message, "Department action failed");
      set({
        staffDepartmentMutationLoading: false,
        staffDepartmentMutationError: message,
      });
      return { success: false, error: message };
    }
  }

  return {
    // GET /api/staff/departments
    fetchStaffDepartments: async () => {
      set({ staffDepartmentsLoading: true, staffDepartmentsError: null });
      try {
        const data = await apiRequest(get, "/api/staff/departments");
        const departments = normalizeDepartments(data);
        set({ staffDepartmentsLoading: false, staffDepartments: departments });
        return { success: true, departments };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch departments");
        set({ staffDepartmentsLoading: false, staffDepartmentsError: message });
        return { success: false, error: message };
      }
    },

    // POST /api/staff/departments
    createStaffDepartment: (payload) =>
      mutate("/api/staff/departments", "POST", payload, "Department created"),

    // PATCH /api/staff/departments/:id
    updateStaffDepartment: (id, payload) =>
      mutate(`/api/staff/departments/${id}`, "PATCH", payload, "Department updated"),

    // DELETE /api/staff/departments/:id
    deleteStaffDepartment: (id) =>
      mutate(`/api/staff/departments/${id}`, "DELETE", undefined, "Department deleted"),
  };
}

const staffDepartmentActions = createStaffDepartmentActions;
export default staffDepartmentActions;
